import React from 'react'
import clsx from 'clsx'
import { Avatar } from '@mui/material'
import { useHistory } from 'react-router'
import { useAppSelector } from '../../../../hooks/redux'
import {
  ArrowRight,
  Call,
  Clock,
  MinusCirlce,
  Profile,
  ProfileCircle,
  Sms
} from 'iconsax-react'
import { useTheme } from '@mui/material'

import OverflowText from '../../OverflowText'
import PhoneFlag from '../../PhoneFlag'
import AvatarsRow from '../../AvatarsRow'
import CloseIcon from '../../../molecules/HamburgerMenuV2/icons/CloseIcon'

import { Routes } from '../../../../Routes'
import {
  convertToTimezone,
  getInitials,
  getShortCode
} from '../../../../utilities'
import { getFormatted } from '../../../../utilities/phoneNumber'
import { formatDate } from '../../../../timeUtilities'
import { selectTimezone } from '../../../../slices/timezone'
import { selectPresence } from '../../../../slices/presence'
import { selectAvatar } from '../../../../slices/avatarSlice'
import useMediaCache from '@SonarCX/hooks/useMediaCache'
import useInterval from '../../../../hooks/useInterval'
import useStyles from './styles'

const statusMap = {
  away: 'Away',
  online: 'Online'
}

const Card = React.forwardRef(
  (
    {
      user,
      close,
      multi = false,
      hideHeader = false,
      users = [],
      onUsersRowClick = undefined,
      rootStyles = undefined
    },
    ref
  ) => {
    const history = useHistory()
    const theme = useTheme()
    const { cache: avatars } = useAppSelector(selectAvatar)
    const { presence } = useAppSelector(selectPresence)
    const { status, media } = useMediaCache()
    const { current } = useAppSelector(selectTimezone)
    const [src, setSrc] = React.useState('')
    const [elem, setElem] = React.useState(null)
    const [currentTime, setCurrentTime] = React.useState(new Date())
    const isExternal = React.useMemo(() => user.type?.external, [user.type])
    const fallbackName = React.useMemo(
      () => getInitials(user.name, 2),
      [user.name]
    )
    const userStatus = React.useMemo(
      () => (presence[user?.id] ? statusMap[presence[user?.id]] : 'Offline'),
      [user, presence]
    )
    const avatarsRef = React.useRef(null)

    useInterval(1000, () => {
      setCurrentTime(new Date())
    })
    const [phones, emails] = React.useMemo(() => {
      if (!user?.channels) {
        return [[], []]
      }

      const phones = []
      const emails = []

      user.channels.forEach(channel => {
        if (channel?.type === 'email') {
          emails.push(channel)
        } else if (['sms_voice', 'voice_only'].includes(channel?.type)) {
          phones.push(channel)
        }
      })

      return [phones, emails]
    }, [user])

    let avatarsOverflowing = false
    if (avatarsRef.current) {
      const element = avatarsRef.current
      avatarsOverflowing = element.offsetWidth < element.scrollWidth
    }
    const classes = useStyles({ avatarsOverflowing })

    // Update avatar
    React.useEffect(() => {
      const { id, avatar_id } = user

      if (avatar_id) {
        if (status[avatar_id] === 'DONE') {
          setSrc(media[avatar_id])
          setElem(null)
        }

        return
      }

      if (avatars[id]) {
        setElem(
          <div
            dangerouslySetInnerHTML={{ __html: avatars[id] }}
            style={{ width: '100%', height: '100%' }}
          />
        )
        setSrc('')
      }
    }, [user, status, media, avatars])

    const stopBubble = React.useCallback(e => {
      e.stopPropagation()
    }, [])

    const handleClose = React.useCallback(
      e => {
        stopBubble(e)
        close(e)
      },
      [stopBubble, close]
    )

    const handleGoTo = React.useCallback(
      e => {
        stopBubble(e)
        const newRoute = isExternal ? Routes.CUSTOMERS : Routes.USERS
        history.push(`${newRoute}/${user.id}`)
      },
      [stopBubble, isExternal, history, user.id]
    )

    return (
      <div ref={ref} className={clsx(rootStyles, classes.root)}>
        <div onClick={stopBubble} className={classes.content}>
          {!hideHeader && (
            <div className={classes.title}>
              <span>{isExternal ? 'Contact' : 'User'} Detail</span>

              <CloseIcon onClick={handleClose} className='close' />
            </div>
          )}
          <div ref={avatarsRef} className={classes.avatars}>
            {!multi ? (
              <Avatar src={src} className={classes.avatar}>
                {elem ?? fallbackName}
              </Avatar>
            ) : (
              <AvatarsRow
                user={user}
                users={users}
                selectUser={u => {
                  onUsersRowClick && onUsersRowClick(u)
                }}
              />
            )}
          </div>

          <div className={classes.rows}>
            <div className={classes.row}>
              <Profile size={theme.size.base} className='icon' variant='Bold' />

              <OverflowText className='label'>
                <label>Name</label>
              </OverflowText>

              <OverflowText className='value'>
                <label>{user.name}</label>
              </OverflowText>
            </div>
            <div className={classes.row}>
              <ProfileCircle
                size={theme.size.base}
                className='icon'
                variant='Bold'
              />
              <OverflowText className='label'>
                <label>Status</label>
              </OverflowText>
              <OverflowText className='value'>
                <label>{userStatus}</label>
              </OverflowText>
            </div>

            <div className={classes.row}>
              <Clock size={theme.size.base} className='icon' variant='Bold' />
              <OverflowText className='label'>
                <label>Local time</label>
              </OverflowText>
              <OverflowText className='value'>
                <label>
                  {' '}
                  {formatDate(
                    convertToTimezone(user?.timezone, currentTime, current),
                    'h:mm a'
                  )}{' '}
                  {getShortCode(user?.timezone, current)}
                </label>
              </OverflowText>
            </div>

            {phones.map(channel => (
              <div key={channel.id} className={classes.row}>
                <Call variant='Bold' size='16' className='icon' />

                <OverflowText className='label'>{channel.name}</OverflowText>

                <PhoneFlag value={channel.value} className='flag' />

                <OverflowText className='value'>
                  {getFormatted(channel.value)}
                </OverflowText>

                {!channel.compliance && (
                  <MinusCirlce
                    variant='Bold'
                    size={theme.size.base}
                    className='compliance'
                  />
                )}
              </div>
            ))}

            {emails.map(channel => (
              <div key={channel.id} className={classes.row}>
                <Sms variant='Bold' size={theme.size.base} className='icon' />

                <OverflowText className='label'>{channel.name}</OverflowText>

                <OverflowText className='value'>
                  {getFormatted(channel.value)}
                </OverflowText>

                {!channel.compliance && (
                  <MinusCirlce
                    variant='Bold'
                    size={theme.size.base}
                    className='compliance'
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        <div onClick={stopBubble} className={classes.bottom}>
          <div onClick={handleGoTo} className={classes.profile}>
            <ProfileCircle variant='Bold' size={theme.size.base} />

            <span>Go to profile</span>

            <ArrowRight size={theme.size.base} />
          </div>
        </div>
      </div>
    )
  }
)

export default React.memo(Card)
